/**
 * What the import will attach, and a drop zone for what it cannot fetch itself.
 *
 * Read-only list first: every post goes into ONE internal journal note; the
 * screenshots and document notes are attached; SharePoint documents too when
 * the account may read the folder. When it may not (the usual case today), the
 * folder link is shown and the reviewer drags the files in — they upload as
 * ordinary draft attachments and travel on `attachment_ids`.
 */

import { ExternalLink, FileText, Icon, Image } from "@norican/icons";
import { Alert, Badge, Field, Typography } from "@norican/react";

import { AttachmentUploader } from "@/components/attachments";
import { SectionCard } from "@/components/common";
import { useT } from "@/i18n";
import type { LegacyFileItem, LegacySummary } from "@/lib/api";

export interface AttachmentsSectionProps {
  summary: LegacySummary;
  onAttachmentIdsChange: (ids: string[]) => void;
  onPendingChange: (pending: boolean) => void;
  disabled?: boolean;
}

export function AttachmentsSection({
  summary,
  onAttachmentIdsChange,
  onPendingChange,
  disabled = false,
}: AttachmentsSectionProps) {
  const { t } = useT("tickets");
  const documents = summary.documents;
  const files: LegacyFileItem[] = [...(summary.note_files ?? []), ...(documents.items ?? [])];

  return (
    <SectionCard title={t("migrate.attachments.title")}>
      <div className="flex flex-col gap-4">
        <Typography variant="body-sm">
          {t("migrate.attachments.note", { n: (summary.posts ?? []).length })}
        </Typography>

        {files.length === 0 ? (
          <Typography variant="body-sm" tone="muted">
            {t("migrate.attachments.none")}
          </Typography>
        ) : (
          <ul className="flex flex-col gap-2">
            {files.map((file, index) => (
              <li key={`${file.source}-${file.name}-${index}`} className="flex items-center gap-2">
                <Icon icon={file.source === "note image" ? Image : FileText} size="sm" />
                <span className="min-w-0 break-words">{file.name}</span>
                {file.allowed === false ? (
                  <Badge tone="warning" variant="subtle" size="sm">
                    {t("migrate.attachments.skipped", { reason: file.reason ?? "" })}
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {documents.available ? null : (
          <Alert tone="info" title={t("migrate.attachments.documentsUnavailable")}>
            {/* Kept here as well as below: "open the folder, then drag the files
                in" reads as one instruction only when the link sits with it. */}
            {documents.folder_url ? (
              <ExternalAnchor
                href={documents.folder_url}
                label={t("migrate.attachments.openFolder")}
              />
            ) : (
              <span>{t("migrate.attachments.noFolder")}</span>
            )}
          </Alert>
        )}

        <Field
          name="legacy-documents"
          label={t("migrate.attachments.dropLabel")}
          hint={t("migrate.attachments.dropHint")}
          disabled={disabled}
        >
          <AttachmentUploader
            id="legacy-documents"
            onChange={onAttachmentIdsChange}
            onPendingChange={onPendingChange}
            disabled={disabled}
          />
        </Field>

        {/* Always offered, not only when the documents could not be read: the
            reviewer checks the legacy ticket and its files before importing. */}
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <ExternalAnchor href={summary.legacy_url} label={t("migrate.summary.openLegacy")} />
          {/* Inside the alert already when the documents could not be read. */}
          {documents.folder_url && documents.available ? (
            <ExternalAnchor
              href={documents.folder_url}
              label={t("migrate.attachments.openFolder")}
            />
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
}

function ExternalAnchor({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-accent-fg-subtle inline-flex items-center gap-1 text-sm hover:underline"
    >
      {label}
      <Icon icon={ExternalLink} size="xs" />
    </a>
  );
}
